import { ArrowUpRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { TrendingAssetsBaseProps, getTypeColor } from './TrendingAssetsBase'
import { ProjectLogo } from './ProjectLogo'
import { formatCompactNumber } from '@/utils'

export const TrendingAssetsDesktop = ({ assets, onAssetClick, loading }: TrendingAssetsBaseProps) => {
  const navigate = useNavigate()
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    
    checkTheme()
    
    const observer = new MutationObserver(checkTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    
    return () => observer.disconnect()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center gap-6">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0" 
          
        >
          Trending Assets
        </h3>
        <div className="flex-1 h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading trending assets...</span>
        </div>
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <div className="flex items-center gap-6">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0" 
          
        >
          Trending Assets
        </h3>
        <div className="flex-1 h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No trending assets available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-6">
      <button 
        onClick={() => navigate('/trading/otc')}
        className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold flex-shrink-0 hover:opacity-80 transition-opacity" 
      >
        Trending Assets
      </button>
      
      <div className="flex-1 h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] overflow-hidden relative">
        <motion.div
          className="flex items-center h-full gap-1.5 pl-1"
          animate={{
            x: [0, -1000],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 30,
              ease: "linear",
            },
          }}
        >
          {/* Double the assets for infinite loop effect */}
          {[...assets, ...assets].map((asset, idx) => (
            <div
              key={`${asset.order_id}-${idx}`}
              className="bg-white dark:bg-[#1c1c1c] rounded-full flex-shrink-0 cursor-pointer hover:shadow-md dark:hover:shadow-none dark:hover:bg-[rgba(253,253,253,0.1)] transition-shadow flex items-center gap-6 pl-1.5 pr-4 py-1"
              onClick={() => onAssetClick?.(asset)}
            >
              <div className="flex items-center gap-2">
                <ProjectLogo logo={asset.logo} alt={asset.project_name} size="sm" />
                <div className="flex items-center gap-2">
                  <span 
                    className="text-sm leading-5 text-[#2d2923] dark:text-[#fdfdfd] font-medium" 
                  >
                    {asset.project_name}
                  </span>
                  <span 
                    className="font-plus-jakarta text-[10px] leading-3 font-semibold"
                    style={{ 
                      color: isDark 
                        ? '#fdfdfd' 
                        : getTypeColor(asset.deal_type) 
                    }}
                  >
                    {asset.deal_type}
                  </span>
                </div>
              </div>
              <div 
                className="text-xs leading-4 font-semibold text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-nowrap flex items-center" 
              >
                ${formatCompactNumber(asset.offered_amount)}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
      
      <button
        onClick={() => navigate('/trading/otc')}
        className="w-[44px] h-[44px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.2)] hover:bg-[rgba(45,41,35,0.12)] dark:hover:bg-[rgba(253,253,253,0.3)] flex items-center justify-center transition-colors flex-shrink-0"
        aria-label="View OTC trading"
      >
        <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
      </button>
    </div>
  )
}

